import { useState } from 'react';
import dayjs from 'dayjs';
import { Box, Button, HStack, IconButton, Skeleton, Text, VStack, useColorModeValue, useToast } from '@chakra-ui/react';
import { MdAdd, MdDeleteOutline } from 'react-icons/md';
import { ConfirmDialog } from '@/design-system/components/ConfirmDialog';
import { useAIChatSessions, useDeleteAIChatSession } from '@/lib/sdk/ai-chat/queries';
import { extractAIError } from '@/lib/sdk/ai-chat/types';
import type { AIChatSession } from '@/lib/sdk/ai-chat/types';

interface SolveSessionsProps {
  /** The session open in the Solve panel, if any */
  currentId: string | null;
  onOpen: (sessionId: string) => void;
  onNew: () => void;
  /** Called after a session is deleted */
  onDeleted: (sessionId: string) => void;
}

/**
 * The user's active Solve sessions, newest first, named by their first message.
 * Opening one switches the Solve panel to it; deleting one frees a session slot.
 */
export function SolveSessions({ currentId, onOpen, onNew, onDeleted }: SolveSessionsProps) {
  const toast = useToast();
  const { data, isLoading } = useAIChatSessions('active');
  const deleteSession = useDeleteAIChatSession();
  const [deleting, setDeleting] = useState<AIChatSession | null>(null);
  const currentBg = useColorModeValue('brand.50', 'gray.700');
  const sessions = (data ?? []).filter((session) => session.session_type === 'solution');

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteSession.mutateAsync(deleting.id);
      onDeleted(deleting.id);
    } catch (error) {
      toast({ status: 'error', title: 'The session could not be deleted', description: extractAIError(error)?.message });
    } finally {
      setDeleting(null);
    }
  };

  return (
    <VStack align="stretch" spacing={2} data-testid="solve-sessions">
      <HStack justify="space-between">
        <Text fontWeight="semibold" fontSize="sm" whiteSpace="nowrap">
          Recent sessions
        </Text>
        <Button size="xs" leftIcon={<MdAdd />} onClick={onNew} isDisabled={!currentId} data-testid="solve-session-new">
          New
        </Button>
      </HStack>
      {isLoading && <Skeleton height="40px" />}
      {!isLoading && sessions.length === 0 && (
        <Text fontSize="xs" color="text-secondary">
          Your requirements from today show here.
        </Text>
      )}
      {sessions.map((session) => (
        <HStack
          key={session.id}
          spacing={1}
          borderRadius="md"
          bg={session.id === currentId ? currentBg : undefined}
          data-testid="solve-session"
          data-current={session.id === currentId}
        >
          <Box
            as="button"
            type="button"
            flex={1}
            minW={0}
            textAlign="left"
            px={2}
            py={1}
            onClick={() => onOpen(session.id)}
            data-testid="solve-session-open"
          >
            <Text fontSize="sm" noOfLines={2}>
              {session.preview || 'New requirement'}
            </Text>
            <Text fontSize="xs" color="text-secondary">
              {dayjs(session.updated_at).format('D MMM, h:mm A')}
            </Text>
          </Box>
          <IconButton
            aria-label="Delete session"
            icon={<MdDeleteOutline size={16} />}
            size="sm"
            variant="ghost"
            onClick={() => setDeleting(session)}
            data-testid="solve-session-delete"
          />
        </HStack>
      ))}
      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title="Delete session"
        message="Delete this session and its plan? This can't be undone."
        confirmText="Delete"
        isLoading={deleteSession.isPending}
      />
    </VStack>
  );
}
